import type { ErrorCode } from '@retailpro/shared';

import { useUi } from '../i18n/ui-context';
import { translateError } from '../i18n/strings';
import { ApiError } from '../lib/api-client';

/**
 * Failure notice.
 *
 * Shows the translated sentence for the error's `code`, because that is the
 * only part of the response guaranteed to be meaningful and stable. The English
 * `detail` is kept in a collapsible line for support: it carries specifics like
 * which field failed, and a cashier cannot act on it, but support can.
 */
export function ErrorNotice({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const { locale, t } = useUi();

  if (!error) return null;

  const code: ErrorCode | string = error instanceof ApiError ? error.code : 'INTERNAL_ERROR';
  const translated = translateError(locale, code);
  const detail = error instanceof ApiError ? error.message : String(error);
  const correlationId = error instanceof ApiError ? error.correlationId : undefined;
  const fieldErrors = error instanceof ApiError ? error.fieldErrors : {};

  return (
    <div className="notice notice--error" role="alert" aria-live="assertive">
      <p className="notice__message">{translated ?? detail}</p>

      {Object.keys(fieldErrors).length > 0 && (
        <ul className="notice__fields">
          {Object.entries(fieldErrors).map(([field, messages]) => (
            <li key={field}>
              <strong>{field}</strong>: {messages.join(' ')}
            </li>
          ))}
        </ul>
      )}

      {translated !== undefined && translated !== detail && (
        <details className="notice__detail">
          <summary>{detail}</summary>
          {correlationId && (
            <p className="notice__correlation">
              {t('common.correlationId')}: <code>{correlationId}</code>
            </p>
          )}
        </details>
      )}

      {onRetry && (
        <button type="button" className="button button--ghost" onClick={onRetry}>
          {t('common.retry')}
        </button>
      )}
    </div>
  );
}
